/**
 * Remote Configuration Types (for SDK v2.0)
 * Match Task 1 API response format exactly
 */

/**
 * API response format from /api/vendors/[vendorId]/sdk-config
 */
export interface ConfigApiResponse {
  success: boolean;
  config: VendorConfig;
}

/**
 * Vendor SDK configuration (matches API response)
 */
export interface VendorConfig {
  features: {
    tracking: TrackingConfig;
    b2cMode?: boolean;  // Auto-group users by their ID for B2C vendors
  };
  version: string;
  updatedAt: string;
}

/**
 * Event tracking feature configuration
 */
export interface TrackingConfig {
  enabled: boolean;
  batchSize?: number;
  flushInterval?: number;
  sampleRate?: number;
}

/**
 * Reason why tracking was paused (e.g., trial expired)
 * Passed to onTrackingPaused callback
 */
export interface TrackingPausedReason {
  code: 'trial_expired' | 'no_subscription' | 'payment_required';
  message: string;
  dashboardUrl: string;
}

/**
 * SDK initialization configuration
 */
export interface InitConfig {
  vendorId: string;
  apiKey: string;
  apiUrl?: string; // Optional override (default: 'https://firstdistro.com')
  persistence?: boolean; // default true - persist user/account context to localStorage
  debug?: boolean; // default false - enable debug logging
  autoCapture?: boolean; // default true - auto-capture $pageview on load + SPA route changes
  /**
   * Allowlist of query-param names to keep on auto-captured $pageview URLs and
   * referrers. Query strings are stripped by default (PII/secret safety);
   * default (undefined/empty) keeps no query params. Case-sensitive, exact match.
   */
  captureQueryParams?: string[];
  /**
   * Code-install hook applied to each auto-captured $pageview url AFTER the
   * default query strip. Returning `null` drops the capture entirely; returning
   * a string replaces the url. Not available via the script-tag install.
   */
  maskUrl?: (url: string) => string | null;
  onTrackingPaused?: (reason: TrackingPausedReason) => void; // Called when tracking is paused (e.g., trial expired)
  signal?: AbortSignal; // Optional abort signal for cancellation during React re-renders
}

/**
 * User context for setup()
 */
export interface SetupUser {
  id: string;
  name?: string;
  email?: string;
  [key: string]: any; // Additional traits
}

/**
 * Account context for setup()
 */
export interface SetupAccount {
  id: string;
  name?: string;
  plan?: string;
  [key: string]: any; // Additional traits
}

/**
 * Options for FirstDistro.setup() - unified context setting
 */
export interface SetupOptions {
  user?: SetupUser;
  account?: SetupAccount;
}

/**
 * EventTracker interface (for Task 3 placeholder)
 */
export interface EventTracker {
  track(event: string, properties?: object): void;
  /** Unified identity setup - sets user/account silently and emits one `$setup` */
  setup(params: {
    userId?: string;
    userTraits?: object;
    accountId?: string;
    accountTraits?: object;
  }): void;
  flush(): void;
  destroy(): void;
}

/**
 * Custom SDK Error Classes
 * Allows for better error handling and distinguishing between error types
 */

/**
 * Base SDK error class
 */
export class SDKError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public retryable: boolean = false
  ) {
    super(message);
    this.name = 'SDKError';
    Object.setPrototypeOf(this, SDKError.prototype);
  }
}

/**
 * Authentication error (invalid API key)
 */
export class AuthenticationError extends SDKError {
  constructor(message: string = 'Invalid API key') {
    super(message, 'AUTHENTICATION_ERROR', 401, false);
    this.name = 'AuthenticationError';
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

/**
 * Not found error (vendor config not found)
 */
export class NotFoundError extends SDKError {
  constructor(message: string = 'Vendor config not found') {
    super(message, 'NOT_FOUND', 404, false);
    this.name = 'NotFoundError';
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

/**
 * Network error (connection failed, timeout, etc.)
 */
export class NetworkError extends SDKError {
  constructor(message: string = 'Network request failed', public originalError?: Error) {
    super(message, 'NETWORK_ERROR', undefined, true);
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Validation error (invalid config structure)
 */
export class ValidationError extends SDKError {
  constructor(message: string = 'Config validation failed') {
    super(message, 'VALIDATION_ERROR', undefined, false);
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * Timeout error (request took too long)
 */
export class TimeoutError extends SDKError {
  constructor(message: string = 'Request timeout', public timeoutMs: number = 10000) {
    super(message, 'TIMEOUT', undefined, true);
    this.name = 'TimeoutError';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}
